import { memo } from 'react';
import { useSelector } from 'react-redux';

import { BoardIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import {
  EditableAssetModalTitle,
  type EditableAssetModalTitleProps,
} from '~/components/AssetModal/components/EditableAssetModalTitle';
import { validateAssetTitle } from '~/components/AssetModalV2/utils';
import { ASSET_TITLE } from '~/constants/testIDs';
import { type CentralizedClipBoardInfo, CentralizedClipSelectors } from '~/store/centralizedClip/selectors';

export type MobileHeaderProps = {
  boardInfo: CentralizedClipBoardInfo;
  onClose?: () => void;
  onUpdateTitle?: EditableAssetModalTitleProps['onSubmit'];
};

export const MobileHeader = memo(({ boardInfo, onClose, onUpdateTitle }: MobileHeaderProps) => {
  const title = useSelector(CentralizedClipSelectors.displayTitle);
  const { current, others } = boardInfo;

  return (
    <div className="flex h-[80px] w-full shrink-0 items-center justify-start ads:bg-base bg-gray-1 p-3">
      <div className="flex h-full flex-col justify-center gap-1 px-3">
        {onUpdateTitle ? (
          <div className="-ml-2.5 flex">
            <EditableAssetModalTitle
              className="grow-0"
              defaultValue={title}
              onSubmit={onUpdateTitle}
              validate={validateAssetTitle}
            />
          </div>
        ) : (
          <h1
            className="flex h-9 w-full shrink-0 items-center truncate font-semibold ads:text-secondary text-18 text-gray-11"
            data-testid={ASSET_TITLE}
          >
            {title}
          </h1>
        )}
        {(!!current || !!others.length) && (
          <div className="flex items-center justify-start">
            {!!current && (
              <div className="ads:text-secondary text-14 text-gray-11">
                {'in'}
                <span className="ml-1 font-medium ads:text-primary text-gray-12">{current.title}</span>
              </div>
            )}
            {!!others.length && (
              <>
                {!!current && <div className="ads:text-primary text-14 text-gray-12">・</div>}
                <BoardIcon className="ads:text-icon-emphasis text-gray-12" size="sm" />
                <div className="ml-1 font-medium ads:text-secondary text-14 text-gray-11">{others.length}</div>
              </>
            )}
          </div>
        )}
      </div>
      {!!onClose && (
        <IconButton
          adsIcon
          icon={XIcon}
          appearance="ghost"
          color="gray"
          size="extra-large"
          onClick={onClose}
          className="ml-auto"
          label="Close modal"
        />
      )}
    </div>
  );
});

MobileHeader.displayName = 'MobileHeader';
